// src/lib/passwordPolicy.js — política de senha forte (única no app · P3 hardening).
// Regra: mínimo 8 caracteres, com minúscula, maiúscula, dígito e símbolo.

export const MIN_SENHA = 8;

/**
 * Valida força da senha. Retorna { ok, msg }.
 * @param {string} senha
 */
export function validarSenhaForte(senha) {
  const s = senha || '';
  if (s.length < MIN_SENHA) return { ok: false, msg: `A senha deve ter ao menos ${MIN_SENHA} caracteres.` };
  if (!/[a-z]/.test(s)) return { ok: false, msg: 'A senha deve conter ao menos uma letra minúscula.' };
  if (!/[A-Z]/.test(s)) return { ok: false, msg: 'A senha deve conter ao menos uma letra maiúscula.' };
  if (!/[0-9]/.test(s)) return { ok: false, msg: 'A senha deve conter ao menos um número.' };
  if (!/[^A-Za-z0-9]/.test(s)) return { ok: false, msg: 'A senha deve conter ao menos um símbolo (ex.: @ # ! $).' };
  return { ok: true, msg: '' };
}

/**
 * Score 0..4 + rótulo/cor para medidor de força (opcional na UI).
 * @param {string} senha
 */
export function forcaSenha(senha) {
  const s = senha || '';
  let score = 0;
  if (s.length >= MIN_SENHA) score++;
  if (/[a-z]/.test(s) && /[A-Z]/.test(s)) score++;
  if (/[0-9]/.test(s)) score++;
  if (/[^A-Za-z0-9]/.test(s)) score++;
  if (s.length >= 12) score = Math.min(4, score + 1);
  const map = [
    { label: 'Muito fraca', color: 'var(--sc-bad)' },
    { label: 'Fraca', color: 'var(--sc-warn)' },
    { label: 'Média', color: 'var(--sc-warn)' },
    { label: 'Boa', color: 'var(--sc-ok)' },
    { label: 'Forte', color: 'var(--sc-ok)' },
  ];
  const i = Math.max(0, Math.min(4, score));
  return { score: i, label: map[i].label, color: map[i].color };
}

/**
 * Checklist ao vivo de requisitos + barra de força, acoplada a um input de senha.
 * Renderiza dentro de #hostId e atualiza a cada tecla. Idempotente e silenciosa
 * (se input/host não existirem, não faz nada).
 * @param {string} inputId  id do <input> de senha
 * @param {string} hostId   id do <div> onde desenhar o feedback
 */
export function mountPasswordChecklist(inputId, hostId) {
  const input = document.getElementById(inputId);
  const host = document.getElementById(hostId);
  if (!input || !host) return;
  const REQS = [
    { label: MIN_SENHA + '+ caracteres', test: (v) => v.length >= MIN_SENHA },
    { label: 'minúscula', test: (v) => /[a-z]/.test(v) },
    { label: 'MAIÚSCULA', test: (v) => /[A-Z]/.test(v) },
    { label: 'número', test: (v) => /[0-9]/.test(v) },
    { label: 'símbolo (@ # ! $…)', test: (v) => /[^A-Za-z0-9]/.test(v) },
  ];
  const draw = () => {
    const v = input.value || '';
    const f = forcaSenha(v);
    host.innerHTML =
      '<div style="display:flex;gap:4px;margin:8px 0 6px">'
      + [0, 1, 2, 3].map((i) => `<span style="flex:1;height:4px;border-radius:2px;background:${i < f.score ? f.color : 'var(--border)'}"></span>`).join('')
      + '</div><div style="display:flex;flex-wrap:wrap;gap:3px 14px">'
      + REQS.map((r) => {
          const ok = r.test(v);
          return `<span style="font-size:11px;display:inline-flex;align-items:center;gap:4px;color:${ok ? 'var(--green,var(--sc-ok))' : 'var(--text3)'};font-weight:${ok ? 600 : 400}">${ok ? '✓' : '○'} ${r.label}</span>`;
        }).join('')
      + '</div>'
      + (v ? `<div style="font-size:11px;margin-top:5px;font-weight:700;color:${f.color}">Força: ${f.label}${f.score >= 3 ? ' — senha confere ✓' : ''}</div>` : '');
  };
  input.addEventListener('input', draw);
  draw();
}

// Texto curto de regra, para placeholders/ajuda.
export const REGRA_SENHA = 'Mín. 8 · maiúscula, minúscula, número e símbolo';
